/**
 * client/src/features/income/components/BucketList/BucketItem.jsx
 * 
 * Individual bucket component that displays a bucket and its income cards.
 * Uses sortable functionality for drag-and-drop repositioning.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { formatCurrency, isSameObjectId, calculateBucketIncome, normalizeIncomeData, isIncomeActive } from '../../utils/incomeUtils';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  HomeIcon,
  GlobeAsiaAustraliaIcon,
  BriefcaseIcon,
  ChartBarIcon,
  SparklesIcon,
  ArrowsUpDownIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  Bars2Icon
} from '@heroicons/react/24/outline';
import SalaryIncomeCard from '../IncomeCards/SalaryIncomeCard';
import ContractorCard from '../IncomeCards/ContractorCard';
import OtherIncomeCard from '../IncomeCards/OtherIncomeCard';
import useBuckets from '../../hooks/useBuckets';
import useIncome from '../../hooks/useIncome';
import IncomeTypeWizard from '../AddIncome/IncomeTypeWizard';

/**
 * Format bucket type for display by removing underscores and capitalizing properly
 */
const formatBucketType = (type) => {
  if (!type) return 'Household';
  
  // Replace underscores with spaces and convert to title case
  return type.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

/**
 * Get appropriate icon based on bucket type
 */
const getBucketIcon = (type) => {
  switch(type) {
    case 'HOUSEHOLD':
      return HomeIcon;
    case 'BUSINESS':
      return BriefcaseIcon;
    case 'INVESTMENT':
      return ChartBarIcon;
    case 'SIDE_HUSTLE':
      return GlobeAsiaAustraliaIcon;
    case 'CUSTOM':
      return SparklesIcon;
    default:
      return BuildingOfficeIcon;
  }
};

// Create a new SortableIncomeCard component
const SortableIncomeCard = ({ income, refreshIncomes, onEdit }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: income._id,
    data: {
      type: 'income',
      income
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative'
  };

  // Render the appropriate card based on income type
  const renderCard = () => {
    const normalizedIncome = normalizeIncomeData(income);
    const type = normalizedIncome.type?.toUpperCase();
    
    console.log(`🔍 Rendering income card for: ${income.name}`, {
      rawType: income.type || income.category || 'UNKNOWN',
      normalizedType: type,
      hasTypeSpecificDetails: !!income.typeSpecificDetails,
      hasSalaryDetails: !!(income.typeSpecificDetails && income.typeSpecificDetails.salary),
      hasContractorDetails: !!(income.typeSpecificDetails && income.typeSpecificDetails.contractor),
      employerName: income.employerName || 'N/A',
      category: income.category || 'N/A'
    });
    
    // Enhanced type detection logic
    if (type === 'SALARY' || 
        income.category === 'SALARY' || 
        income.type === 'SALARY' ||
        (income.typeSpecificDetails && income.typeSpecificDetails.salary) ||
        income.employerName) {
      console.log('✅ Rendering SalaryIncomeCard for:', income.name);
      return <SalaryIncomeCard 
        income={income} 
        refreshIncomes={refreshIncomes} 
        onEdit={onEdit} 
      />;
    } else if (type === 'CONTRACTOR' || 
              type === 'CONTRACT' || 
              income.category === 'CONTRACT' || 
              income.type === 'CONTRACT' ||
              income.category === 'CONTRACTOR' || 
              income.type === 'CONTRACTOR' ||
              (income.typeSpecificDetails && income.typeSpecificDetails.contractor)) {
      console.log('✅ Rendering ContractorCard for:', income.name);
      return <ContractorCard 
        income={income} 
        refreshIncomes={refreshIncomes} 
        onEdit={onEdit} 
      />;
    } else {
      console.log('✅ Rendering OtherIncomeCard for:', income.name);
      return <OtherIncomeCard 
        income={income} 
        refreshIncomes={refreshIncomes} 
        onEdit={onEdit} 
      />;
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 p-1 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
        title="Drag to reorder"
      >
        <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" />
      </div>
      {/* Card Content */}
      <div className="pl-4">
        {renderCard()}
      </div>
    </div>
  );
};

/**
 * BucketItem Component
 * 
 * Displays a single income bucket with expandable content.
 * 
 * @param {Object} props
 * @param {Object} props.bucket - Bucket object with name and ID
 * @param {Array} props.incomes - List of income objects for this bucket
 * @param {Object} props.monthlyTotal - Calculated monthly totals
 * @param {Function} props.onAddIncome - Callback when adding income
 * @param {Function} props.refreshIncomes - Function to refresh income data
 * @param {Function} props.onBucketDeleted - Callback when bucket is deleted
 */
const BucketItem = ({ 
  bucket, 
  incomes = [], 
  monthlyTotal = {}, 
  onAddIncome, 
  refreshIncomes,
  onBucketDeleted,
  totalBuckets = 1
}) => {
  // Add console log to debug what incomes are being received
  console.log(`🔍 BucketItem for ${bucket.name} (${bucket._id}):`, { 
    bucketIncomes: incomes, 
    bucketIncomesCount: incomes ? incomes.length : 0,
    bucket,
    totalBuckets
  });

  // State for expanded/collapsed view - auto-expand if only one bucket exists
  const [isExpanded, setIsExpanded] = useState(bucket.isExpanded || totalBuckets === 1);
  // State for income type wizard modal
  const [showIncomeTypeWizard, setShowIncomeTypeWizard] = useState(false);
  
  // Auto-expand when only one bucket exists
  useEffect(() => {
    if (totalBuckets === 1 && !isExpanded) {
      setIsExpanded(true);
    }
  }, [totalBuckets, isExpanded]);
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showIncomeWizard, setShowIncomeWizard] = useState(false);
  
  // Add state for editing income
  const [editingIncome, setEditingIncome] = useState(null);
  
  // Get bucket management functions from custom hooks
  const { deleteBucket, toggleBucketExpansion } = useBuckets();
  const { reorderIncomes } = useIncome();
  
  // Setup draggable functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: bucket._id,
    data: {
      type: 'bucket',
      bucket
    }
  });
  
  // Apply drag-and-drop styles
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1
  };
  
  // Filter incomes that belong to this bucket using robust ObjectId comparison
  const bucketIncomes = useMemo(() => {
    if (!incomes || !incomes.length) return [];
    
    // Use the utility to compare IDs regardless of format
    const filteredIncomes = incomes.filter(income => 
      isSameObjectId(income.bucketId, bucket._id) || 
      isSameObjectId(income.bucket, bucket._id)
    );
    
    // Partition active vs inactive
    const active = [];
    const inactive = [];
    for (const inc of filteredIncomes) {
      (isIncomeActive(inc) ? active : inactive).push(inc);
    }

    // Sort: active by order asc, inactive by endDate desc then order asc
    const byOrder = (a, b) => ((a.order ?? 0) - (b.order ?? 0));
    active.sort(byOrder);
    inactive.sort((a, b) => {
      const aEnd = a.endDate ? new Date(a.endDate).getTime() : 0;
      const bEnd = b.endDate ? new Date(b.endDate).getTime() : 0;
      if (aEnd !== bEnd) return bEnd - aEnd; // most recently ended first
      return byOrder(a, b);
    });
    
    return [...active, ...inactive];
  }, [incomes, bucket._id]);
  
  // Handle expand/collapse toggle
  const handleToggleExpand = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    toggleBucketExpansion(bucket._id, newExpandedState);
  };
  
  // Handle bucket deletion
  const handleDeleteBucket = async () => {
    try {
      setIsDeleting(true);
      console.log(`🗑️ Attempting to delete bucket: ${bucket.name} (${bucket._id})`);
      
      // Call the parent's onBucketDeleted callback
      if (onBucketDeleted) {
        await onBucketDeleted(bucket._id, bucket.name);
      }
      
      // Hide the confirm dialog
      setShowDeleteConfirm(false);
      
      console.log(`✅ Successfully deleted bucket: ${bucket.name}`);
    } catch (err) {
      console.error(`❌ Error deleting bucket ${bucket._id}:`, err);
      alert(`Error occurred while deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle add income button click
  const handleAddIncome = () => {
    setShowIncomeTypeWizard(true);
  };
  
  // Handle income type wizard close
  const handleWizardClose = () => {
    setShowIncomeTypeWizard(false);
  };
  
  // Handle income creation success
  const handleIncomeSuccess = async (newIncome) => {
    console.log('✅ Income added successfully to bucket:', { bucketId: bucket._id, newIncome });
    
    // Close the wizard
    setShowIncomeTypeWizard(false);
    
    // Refresh incomes to show the new entry
    if (refreshIncomes) {
      await refreshIncomes();
    }
  };

  // Add handler for editing income
  const handleEditIncome = (income) => {
    console.log('BucketItem: Opening edit form with income data:', income);
    // Ensure we have complete income data to pass to the form
    if (!income) {
      console.error('Cannot edit income: No income data provided');
      return;
    }
    
    // Set the income for editing
    setEditingIncome(income);
    
    // Show income type wizard with editing flag
    setShowIncomeTypeWizard(true);
  };
  
  // Handle successful income edit
  const handleEditSuccess = (updatedIncome) => {
    setEditingIncome(null);
    setShowIncomeTypeWizard(false);
    if (refreshIncomes) {
      refreshIncomes();
    }
  };
  
  // Handle cancelling the edit
  const handleEditCancel = () => {
    setEditingIncome(null);
    setShowIncomeTypeWizard(false);
  };
  
  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for incomes
  const handleIncomeDragEnd = async (event) => {
    console.log('🔄 Income drag end event:', event);
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    // Use bucketIncomes instead of incomes to ensure we're working with the correct income objects
    const oldIndex = bucketIncomes.findIndex(income => income._id === active.id);
    const newIndex = bucketIncomes.findIndex(income => income._id === over.id);

    console.log('📊 Income reorder indexes:', {
      oldIndex,
      newIndex,
      totalIncomes: bucketIncomes.length,
      activeIncome: bucketIncomes[oldIndex],
      overIncome: bucketIncomes[newIndex]
    });

    // Log the active and over IDs for debugging
    console.log('🔍 Active and Over IDs:', {
      activeId: active.id,
      overId: over.id,
      activeIncomeId: bucketIncomes[oldIndex]?._id,
      overIncomeId: bucketIncomes[newIndex]?._id
    });

    // Create a new array with the updated order using bucketIncomes
    const reorderedIncomes = arrayMove(bucketIncomes, oldIndex, newIndex);
    
    // Log the reordered incomes for debugging
    console.log('🔍 Reordered incomes before mapping:', reorderedIncomes);
    
    // Make sure we're sending the complete income objects with their IDs
    const orderUpdates = reorderedIncomes.map((income, index) => {
      // Log each income object to verify it has an _id
      console.log(`🔍 Income ${index}:`, { _id: income._id, name: income.name });
      
      return {
        _id: income._id,
        order: index
      };
    });

    console.log('🔄 Reordered incomes:', orderUpdates);

    try {
      console.log('🔄 Calling reorderIncomes API with bucketId:', bucket._id);
      await reorderIncomes(bucket._id, orderUpdates);
      console.log('✅ API call to reorderIncomes completed successfully');
      
      // Refresh the incomes to get the updated order
      console.log('🔄 Refreshing incomes after reorder');
      await refreshIncomes();
    } catch (error) {
      console.error('❌ Error reordering incomes:', error);
      // Refresh incomes to restore the original order
      console.log('🔄 Refreshing incomes after error');
      await refreshIncomes();
    }
  };

  const { gross = 0, net = 0 } = monthlyTotal;
  
  // Format bucket type for display
  const formattedType = formatBucketType(bucket.type);
  
  // Log the totals for debugging
  console.log(`📊 Bucket ${bucket.name} totals - Gross: $${gross.toLocaleString()}, Net: $${net.toLocaleString()}`);
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all duration-200"
    >
      {/* Bucket Header - Enhanced with larger height and more details */}
      <div className="p-3 sm:p-4 md:p-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Top Row - Bucket Info and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            {/* Drag handle */}
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 rounded-md hover:bg-gray-100 flex-shrink-0"
            >
              <Bars2Icon className="w-4 h-4 text-gray-400" />
            </div>
            
              {/* Bucket Icon and Name */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {React.createElement(getBucketIcon(bucket.type), { className: "w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 flex-shrink-0" })}
            <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-base sm:text-lg text-gray-900 truncate">{bucket.name}</span>
                  <span className="text-xs sm:text-sm text-indigo-600">{formattedType}</span>
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Mobile Optimized */}
            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
              <button 
                onClick={handleAddIncome}
                className="min-h-[44px] px-2 sm:px-3 py-2 flex items-center gap-1 text-xs sm:text-sm font-medium rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Add Income</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button 
                onClick={() => setShowDeleteConfirm(true)}
                className="min-h-[44px] px-2 sm:px-3 py-2 flex items-center gap-1 text-xs sm:text-sm font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
          
          {/* Enhanced Details Row - Mobile Responsive Grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {/* Monthly Gross */}
            <div className="bg-gray-50 p-2 sm:p-3 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Monthly Gross</div>
              <div className="text-sm sm:text-xl font-bold text-gray-900">{formatCurrency(gross)}</div>
            </div>
            
            {/* Monthly Net */}
            <div className="bg-indigo-50 p-2 sm:p-3 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Monthly Net</div>
              <div className="text-sm sm:text-xl font-bold text-indigo-600">{formatCurrency(net)}</div>
            </div>
            
            {/* Income Sources Count */}
            <div className="bg-green-50 p-2 sm:p-3 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Sources</div>
              <div className="text-sm sm:text-xl font-bold text-green-600">{bucketIncomes.length}</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Optimized Expand/Collapse Bar */}
      <button 
        onClick={handleToggleExpand}
        className={`w-full px-4 py-3 sm:py-4 flex items-center justify-center gap-2 text-white font-medium transition-all duration-200 min-h-[48px] ${
          isExpanded 
            ? 'bg-purple-700 hover:bg-purple-800' 
            : 'bg-purple-600 hover:bg-purple-700'
        }`}
      >
        <ChevronUpIcon
          className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200 ${
            isExpanded ? 'transform rotate-180' : ''
          }`}
        />
        <span className="text-sm">
          {isExpanded ? 'Close' : 'View Details'}
        </span>
      </button>

      {/* Bucket Content - Mobile Optimized */}
      {isExpanded && (
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50">
          {/* Income Cards Section - Enhanced with drag-and-drop */}
          <div className="space-y-3 sm:space-y-4 mb-4">
            <h3 className="text-sm font-medium text-gray-500">Income Sources ({bucketIncomes.length})</h3>
            
            {/* Hide debug info in production and make it less prominent */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-400 mb-2 hidden sm:block">
                Bucket ID: {bucket._id}, Has Incomes: {bucketIncomes.length > 0 ? 'Yes' : 'No'}
              </div>
            )}
            
            {/* Check if there are incomes to display */}
            {bucketIncomes && bucketIncomes.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleIncomeDragEnd}
              >
                <SortableContext 
                  items={bucketIncomes.map(inc => inc._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 sm:space-y-3">
                    {bucketIncomes.map(income => (
                      <SortableIncomeCard
                        key={income._id}
                        income={income}
                        refreshIncomes={refreshIncomes}
                        onEdit={() => handleEditIncome(income)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <p className="text-gray-500 text-sm py-4 text-center bg-gray-50 rounded-lg">
                No income sources added yet
              </p>
            )}
          </div>

          {/* Debug Information - Hide completely on mobile, only show in development */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-4 text-xs p-2 bg-gray-100 rounded-md hidden sm:block">
              <summary className="cursor-pointer font-mono text-gray-600">Debug: Income Data</summary>
              <pre className="p-2 mt-2 bg-gray-800 text-green-400 rounded overflow-auto max-h-60">
                {JSON.stringify({
                  bucket: {
                    _id: bucket._id,
                    name: bucket.name,
                    type: bucket.type
                  },
                  totals: {
                    gross: formatCurrency(gross),
                    net: formatCurrency(net),
                    difference: formatCurrency(gross - net)
                  },
                  incomesCount: bucketIncomes.length,
                  incomes: bucketIncomes.map(inc => ({
                    _id: inc._id,
                    name: inc.name,
                    grossAmount: inc.grossAmount || inc.amount,
                    netAmount: inc.netAmount,
                    typeSpecificData: {
                      hasData: inc.typeSpecificDetails?.salary ? 'Yes' : 'No',
                      grossAmount: inc.typeSpecificDetails?.salary?.payDetails?.grossAmount,
                      netAmount: inc.typeSpecificDetails?.salary?.payDetails?.netAmount
                    },
                    type: inc.type,
                    bucket: typeof inc.bucket === 'object' 
                      ? `ObjectId('${inc.bucket?._id || String(inc.bucket)}')`
                      : inc.bucket,
                    bucketId: inc.bucketId
                  }))
                }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
      
      {/* Modal for editing income */}
      {showIncomeTypeWizard && (
      <IncomeTypeWizard
        isOpen={showIncomeTypeWizard}
          onClose={() => {
            setShowIncomeTypeWizard(false);
            setEditingIncome(null);
          }}
        bucketId={bucket._id}
        bucketName={bucket.name}
        onSuccess={handleIncomeSuccess}
          income={editingIncome}
      />
      )}
      
      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center text-red-600 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{bucket.name}</span>? This action will also delete all income entries in this bucket and cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBucket}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 rounded-lg flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Delete Bucket
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BucketItem; 